import { Alert, AlertTitle, Box, Button, Link, List, ListItem, SxProps, Typography, useTheme } from '@mui/material';
import React from 'react';

import { TNotificationAlert } from '../../../types/addressBook';

interface IProps {
  type: TNotificationAlert;
  children: React.ReactNode;
  sx?: SxProps;
  hideAlertIcon?: boolean;
  actionButtonText?: string;
  actionOnClick?: () => void;
  actionSx?: SxProps;
}

interface ITitleProps {
  children: React.ReactNode;
  sx?: SxProps;
}

interface IDescriptionProps {
  children: React.ReactNode;
  sx?: SxProps;
}

interface ILinkProps {
  href: string;
  children: React.ReactNode;
  download?: boolean;
  icon?: React.ReactElement;
  sx?: SxProps;
}

interface IListProps {
  children: React.ReactNode;
  sx?: SxProps;
}

interface IListItemProps {
  children: React.ReactNode;
  sx?: SxProps;
}

const NotificationAlert: React.FC<IProps> & {
  Title: React.FC<ITitleProps>;
  Description: React.FC<IDescriptionProps>;
  Link: React.FC<ILinkProps>;
  List: React.FC<IListProps> & {
    ListItem: React.FC<IListItemProps>;
  };
} = ({ type, children, sx, hideAlertIcon = false, actionButtonText, actionOnClick, actionSx }) => {
  const actionContent = actionButtonText ? (
    <Button color="inherit" size="small" onClick={actionOnClick} data-action={'alert_action_btn'}>
      {actionButtonText}
    </Button>
  ) : null;

  return (
    <Alert
      severity={type}
      action={actionContent}
      sx={{
        borderRadius: 0.5,
        ...sx,
      }}
      icon={hideAlertIcon ? false : undefined}
      slotProps={
        actionContent
          ? {
              action: {
                sx: {
                  minWidth: '132px',
                  whiteSpace: 'nowrap',
                  ...actionSx,
                },
              },
            }
          : undefined
      }
    >
      {children}
    </Alert>
  );
};

const Title: React.FC<ITitleProps> = ({ children, sx }) => {
  return (
    <AlertTitle variant="body1" sx={sx}>
      {children}
    </AlertTitle>
  );
};

const Description: React.FC<IDescriptionProps> = ({ children, sx }) => {
  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-line',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

const LinkComponent: React.FC<ILinkProps> = ({ href, children, download = false, icon, sx }) => {
  const theme = useTheme();

  const styledIcon = icon
    ? React.cloneElement(icon, {
        sx: { ml: 1, width: 20, height: 20, ...icon.props.sx },
      })
    : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      <Link
        href={href}
        download={download}
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'info.main',
          fontWeight: theme.typography.fontWeightMedium,
          mt: 1,
          textDecoration: 'none',
          ...sx,
        }}
      >
        {children}
        {styledIcon}
      </Link>
    </Box>
  );
};

const ListComponent: React.FC<IListProps> = ({ children, sx }) => {
  return (
    <List
      sx={{
        listStyleType: 'disc',
        listStylePosition: 'outside',
        pl: 2,
        ...sx,
      }}
    >
      {children}
    </List>
  );
};

const ListItemComponent: React.FC<IListItemProps> = ({ children, sx }) => {
  return (
    <ListItem
      sx={{
        display: 'list-item',
        py: 0,
        pl: 1,
        ...sx,
      }}
    >
      {children}
    </ListItem>
  );
};

NotificationAlert.Title = Title;
NotificationAlert.Description = Description;
NotificationAlert.Link = LinkComponent;

const ListWithItem = ListComponent as typeof ListComponent & { ListItem: typeof ListItemComponent };

ListWithItem.ListItem = ListItemComponent;
NotificationAlert.List = ListWithItem;

export {
  Description as AlertDescription,
  LinkComponent as AlertLink,
  ListComponent as AlertList,
  ListItemComponent as AlertListItem,
  Title as AlertTitle,
};

export default NotificationAlert;
